/**
 * VÉRAULT Portal — Client Notifications Bell (Wave 36 · PI-9)
 *
 * Tiny calm bell in the client portal header. Shows unread count;
 * dropdown lists most-recent reminders sent by the advisor. Clicking a
 * notification marks it read; the bell badge updates in place.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Bell, CheckCheck, X } from "lucide-react";
import { api } from "../../../index";

function fmtRel(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  } catch {
    return "";
  }
}

export default function NotificationsBell({ tripId }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [unread, setUnread] = useState(0);
  const wrapRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const url = tripId ? `/notifications/inbox?trip_id=${tripId}` : "/notifications/inbox";
      const { data } = await api.get(url);
      setRows(data?.rows || []);
      setUnread(data?.unread || 0);
    } catch {
      setRows([]);
      setUnread(0);
    }
  }, [tripId]);

  useEffect(() => {
    load();
    // gentle polling — once a minute is plenty for in-portal reminders.
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  // close on outside click
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const markRead = async (id) => {
    try {
      await api.post(`/notifications/${id}/read`);
      setRows((rs) => rs.map((r) => (r.id === id ? { ...r, read_at: new Date().toISOString() } : r)));
      setUnread((u) => Math.max(0, u - 1));
    } catch {
      /* non-blocking */
    }
  };

  const dismiss = async (id) => {
    try {
      await api.post(`/notifications/${id}/dismiss`);
      setRows((rs) => rs.filter((r) => r.id !== id));
    } catch {
      /* non-blocking */
    }
  };

  const markAll = async () => {
    try {
      await api.post(`/notifications/inbox/mark-all-read${tripId ? `?trip_id=${tripId}` : ""}`);
      setRows((rs) => rs.map((r) => ({ ...r, read_at: r.read_at || new Date().toISOString() })));
      setUnread(0);
    } catch {
      /* non-blocking */
    }
  };

  return (
    <div ref={wrapRef} className="relative" data-testid="client-notifications-bell">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Notifications"
        className="relative inline-flex items-center justify-center size-8 rounded-full hover:bg-[hsl(var(--background-paper))] transition-colors"
      >
        <Bell className="size-4 text-[hsl(var(--foreground-secondary))]" strokeWidth={1.5} />
        {unread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 grid place-items-center rounded-full bg-[hsl(var(--accent-gold))] text-[hsl(var(--background-deep))] font-mono text-[10px] leading-none"
            data-testid="client-notifications-unread-count"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-2 z-40 w-[320px] max-w-[90vw] rounded-md border border-[hsl(var(--surface-hairline))] bg-[hsl(var(--background-elevated))] shadow-lg overflow-hidden"
          data-testid="client-notifications-dropdown"
        >
          <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-[hsl(var(--surface-hairline))]">
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
              Notifications
            </p>
            {unread > 0 && (
              <button
                type="button"
                onClick={markAll}
                className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))]"
                data-testid="client-notifications-mark-all"
              >
                <CheckCheck className="size-3" /> Mark all
              </button>
            )}
          </div>
          <div className="max-h-[420px] overflow-y-auto">
            {rows.length === 0 ? (
              <p className="px-3 py-6 font-accent italic text-[13px] text-[hsl(var(--foreground-muted))] text-center">
                Nothing waiting on you.
              </p>
            ) : (
              rows.map((r) => (
                <div
                  key={r.id}
                  onClick={() => !r.read_at && markRead(r.id)}
                  className={`group relative px-3 py-3 border-b border-[hsl(var(--surface-hairline))] last:border-0 cursor-pointer transition-colors ${
                    r.read_at ? "" : "bg-[hsl(var(--accent-gold))]/5"
                  }`}
                  data-testid={`client-notification-${r.id}`}
                >
                  <div className="flex items-start gap-2">
                    {!r.read_at && <span className="size-1.5 rounded-full bg-[hsl(var(--accent-gold))] mt-1.5 shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <p className="font-serif text-[13.5px] leading-snug">{r.title}</p>
                      {r.body && (
                        <p className="mt-1 font-accent italic text-[12px] text-[hsl(var(--foreground-muted))] line-clamp-3">
                          {r.body}
                        </p>
                      )}
                      <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">
                        {fmtRel(r.created_at)}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); dismiss(r.id); }}
                      className="opacity-0 group-hover:opacity-100 size-5 grid place-items-center rounded hover:bg-[hsl(var(--background-paper))] text-[hsl(var(--foreground-muted))]"
                      aria-label="Dismiss"
                    >
                      <X className="size-3" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
